"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import { getAssistantOptions, updateAssistantOptions } from "@/lib/storage";
import { useLocale } from "./localeContext";
import { useAppEvent } from "@/lib/events";
import { ClientLogger } from "@/lib/clientLogger";

export type McpServer = {
  name: string;
  url: string;
  startupEnabled: boolean;
  required: boolean;
  startupForLocales?: string[];
  // When true the composer renders a per-thread date filter on this server's
  // chip (see mcpServerDateFiltersContext). Surfaced by GET /api/mcp-servers
  // from `McpServerConfig.DateFilter`. Defaults to false for back-compat with
  // a backend that doesn't yet send the field.
  dateFilter?: boolean;
};

// Health status reported by the validate endpoint:
//   validating — request in flight
//   healthy    — list-tools succeeded
//   error      — transient failure (timeout / 5xx / unknown). Retry button shown.
//   paywalled  — 402 Payment Required. Terminal for this tenant — no retry, modal
//                copy points the user at sales.
export type McpServerHealth = {
  status: "validating" | "healthy" | "error" | "paywalled";
  error?: string;
  toolCount?: number;
};

export type McpValidationResultRow = {
  name: string;
  status: McpServerHealth["status"];
  toolCount?: number;
  error?: string;
};

type SourceUnavailableState = {
  open: boolean;
  serverName: string;
  mode: "paywalled" | "error";
  errorDetail?: string;
};

type McpServersContextValue = {
  allServers: McpServer[];
  selectedServers: string[];
  serverHealth: Record<string, McpServerHealth>;
  toggleServer: (name: string) => Promise<McpValidationResultRow | undefined>;
  setThreadId: (threadId: string | undefined) => void;
  validateServers: (names: string[]) => Promise<McpValidationResultRow[]>;
  sourceUnavailable: SourceUnavailableState;
  openSourceUnavailable: (serverName: string, mode: "paywalled" | "error", errorDetail?: string) => void;
  closeSourceUnavailable: () => void;
};

const McpServersContext = createContext<McpServersContextValue>({
  allServers: [],
  selectedServers: [],
  serverHealth: {},
  toggleServer: async () => undefined,
  setThreadId: () => {},
  validateServers: async () => [],
  sourceUnavailable: { open: false, serverName: "", mode: "error" },
  openSourceUnavailable: () => {},
  closeSourceUnavailable: () => {},
});

function getDefaults(servers: McpServer[], lang: string): string[] {
  return servers
    .filter(s => s.startupEnabled || s.startupForLocales?.includes(lang))
    .map(s => s.name);
}

function normalizeStatus(raw: string): McpServerHealth["status"] {
  switch (raw) {
    case "healthy":
    case "paywalled":
    case "validating":
    case "error":
      return raw;
    default:
      return "error";
  }
}

export function McpServersProvider({ children }: { children: ReactNode }) {
  const { lang } = useLocale();
  const [allServers, setAllServers] = useState<McpServer[]>([]);
  const [selectedServers, setSelectedServers] = useState<string[]>([]);
  const [serverHealth, setServerHealth] = useState<Record<string, McpServerHealth>>({});
  const [threadId, setThreadIdState] = useState<string | undefined>();
  const [sourceUnavailable, setSourceUnavailable] = useState<SourceUnavailableState>({
    open: false,
    serverName: "",
    mode: "error",
  });
  const allServersRef = useRef(allServers);
  allServersRef.current = allServers;
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const langRef = useRef(lang);
  langRef.current = lang;
  // Synchronous read of selectedServers — toggleServer needs to derive isAdding
  // without waiting for React state to flush, otherwise rapid clicks race.
  const selectedServersRef = useRef(selectedServers);
  selectedServersRef.current = selectedServers;

  const validateServers = useCallback(async (names: string[]): Promise<McpValidationResultRow[]> => {
    if (names.length === 0) return [];

    setServerHealth(prev => {
      const next = { ...prev };
      for (const name of names) {
        next[name] = { status: "validating" };
      }
      return next;
    });

    try {
      const res = await apiAuthFetch("api/mcp-servers/validate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ servers: names }),
      });

      if (!res.ok) {
        const failure: McpValidationResultRow[] = names.map(name => ({
          name,
          status: "error",
          error: `Validation request failed: ${res.status}`,
        }));
        setServerHealth(prev => {
          const next = { ...prev };
          for (const row of failure) next[row.name] = { status: "error", error: row.error };
          return next;
        });
        return failure;
      }

      const data = await res.json();
      const results = (data.results as { name: string; status: string; toolCount?: number; error?: string }[])
        .map<McpValidationResultRow>(r => ({
          name: r.name,
          status: normalizeStatus(r.status),
          toolCount: r.toolCount ?? undefined,
          error: r.error,
        }));

      setServerHealth(prev => {
        const next = { ...prev };
        for (const result of results) {
          next[result.name] = {
            status: result.status,
            error: result.error,
            toolCount: result.toolCount,
          };
        }
        return next;
      });
      return results;
    } catch (err) {
      const failure: McpValidationResultRow[] = names.map(name => ({
        name,
        status: "error",
        error: String(err),
      }));
      setServerHealth(prev => {
        const next = { ...prev };
        for (const row of failure) next[row.name] = { status: "error", error: row.error };
        return next;
      });
      return failure;
    }
  }, []);

  useEffect(() => {
    apiAuthFetch("api/mcp-servers?startupEnabled=false")
      .then(res => res.json())
      .then((servers: McpServer[]) => {
        setAllServers(servers);
        const defaults = getDefaults(servers, lang);
        setSelectedServers(defaults);
        selectedServersRef.current = defaults;

        // Validate all default servers (including required ones like praetor)
        if (defaults.length > 0) {
          validateServers(defaults);
        }
      })
      .catch(err => {
        ClientLogger.error("Failed to fetch MCP servers", {
          error: err, component: "McpServersContext",
        });
      });
  }, [lang, validateServers]);

  // Listen for chat-time MCP server unavailable events
  useAppEvent("mcp-server-unavailable", (servers) => {
    if (!servers?.length) return;
    setServerHealth(prev => {
      const next = { ...prev };
      for (const { serverName, errorMessage } of servers) {
        next[serverName] = { status: "error", error: errorMessage };
      }
      return next;
    });
  });

  const setThreadId = useCallback((id: string | undefined) => {
    setThreadIdState(id);
    if (allServersRef.current.length === 0) return;
    const defaults = getDefaults(allServersRef.current, langRef.current);
    const stored = getAssistantOptions(id).selectedMcpServers;
    const next = stored.length > 0 ? stored : defaults;
    setSelectedServers(next);
    selectedServersRef.current = next;
  }, []);

  const toggleServer = useCallback(async (name: string): Promise<McpValidationResultRow | undefined> => {
    const current = selectedServersRef.current;
    const isAdding = !current.includes(name);
    const next = isAdding ? [...current, name] : current.filter(n => n !== name);
    setSelectedServers(next);
    selectedServersRef.current = next;
    updateAssistantOptions(threadIdRef.current, { selectedMcpServers: next });

    if (!isAdding) {
      setServerHealth(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
      return undefined;
    }

    const results = await validateServers([name]);
    return results.find(r => r.name === name);
  }, [validateServers]);

  const openSourceUnavailable = useCallback((serverName: string, mode: "paywalled" | "error", errorDetail?: string) => {
    setSourceUnavailable({ open: true, serverName, mode, errorDetail });
  }, []);

  const closeSourceUnavailable = useCallback(() => {
    setSourceUnavailable(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <McpServersContext.Provider value={{
      allServers,
      selectedServers,
      serverHealth,
      toggleServer,
      setThreadId,
      validateServers,
      sourceUnavailable,
      openSourceUnavailable,
      closeSourceUnavailable,
    }}>
      {children}
    </McpServersContext.Provider>
  );
}

export function useMcpServers() {
  return useContext(McpServersContext);
}
